"use client";
import type { IImage } from "@components/types/media";
import type React from "react";

import { AspectRatio } from "@components/core/AspectRatio";
import { Button } from "@components/core/Button";
import { Image } from "@components/core/Image";
import { Range } from "@components/core/Range";
import Pause24 from "@ui/icons/24x24/pause-24.svg";
import Play24 from "@ui/icons/24x24/play-24.svg";
import Volume24 from "@ui/icons/24x24/volume-24.svg";
import VolumeMute24 from "@ui/icons/24x24/volume-mute-24.svg";
import { isValidImage } from "@ui/lib/image";
import { logger } from "@utilities/logger";
import { formatTime } from "@ui/lib/time";
import { cn } from "@ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";

interface IControlsProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  children?: React.ReactNode;
  className?: string;
  isPlaying: boolean;
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>;
}

const Controls = ({ isPlaying, setIsPlaying, audioRef, children, className }: IControlsProps) => {
  const [volume, setVolume] = useState(60);
  const [muteVolume, setMuteVolume] = useState(false);
  const playAnimationRef = useRef<number>(undefined);

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  const repeat = useCallback(() => {
    if (audioRef.current) {
      playAnimationRef.current = requestAnimationFrame(repeat);
    }
  }, [audioRef]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current
        .play()
        .then(() => {
          playAnimationRef.current = requestAnimationFrame(repeat);
        })
        .catch(err => {
          logger.error(err instanceof Error ? err : null, "Unable to play audio");
          setIsPlaying(false);
        });
    } else {
      audioRef.current.pause();
      if (playAnimationRef.current) {
        cancelAnimationFrame(playAnimationRef.current);
      }
    }

    return () => (playAnimationRef.current ? cancelAnimationFrame(playAnimationRef.current) : undefined);
  }, [isPlaying, audioRef, repeat, setIsPlaying]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
      audioRef.current.muted = muteVolume;
    }
  }, [volume, audioRef, muteVolume]);

  return (
    <div className={cn("flex items-center gap-8", className)}>
      {isPlaying ? (
        <Button aria-label='Pause' icon={<Pause24 />} onClick={togglePlayPause} size='pill-sm' variant='ghost' />
      ) : (
        <Button aria-label='Play' icon={<Play24 />} onClick={togglePlayPause} size='pill-sm' variant='ghost' />
      )}
      {children}
      <div className='flex items-center gap-x-8'>
        <Button
          aria-label='Mute'
          icon={muteVolume ? <VolumeMute24 /> : <Volume24 />}
          onClick={() => setMuteVolume(prev => !prev)}
          size='pill-sm'
          variant='ghost'
        />
        <Range
          className='w-60'
          defaultValue={[volume]}
          max={100}
          min={0}
          name='audio-volume'
          onValueChange={([val]) => setVolume(val)}
        />
      </div>
    </div>
  );
};

Controls.displayName = "Controls";

interface IProgressBarProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  className?: string;
  duration: number;
  isPlaying: boolean;
}

const ProgressBar = ({ isPlaying, duration, audioRef, className }: IProgressBarProps) => {
  const [time, setTime] = useState(audioRef.current?.currentTime || 0);
  const [progress, setProgress] = useState(0);
  const requestRef = useRef<number>(undefined);

  const setProgressTime = useCallback(() => {
    if (audioRef.current) {
      const currentTime = audioRef.current.currentTime;
      setTime(currentTime);
      setProgress((currentTime / duration) * 100);
    }
  }, [audioRef, duration]);

  const updateTime = useCallback(() => {
    if (audioRef.current) {
      setProgressTime();
      requestRef.current = requestAnimationFrame(updateTime);
    }
  }, [audioRef, setProgressTime]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isPlaying) {
      requestRef.current = requestAnimationFrame(updateTime);
    } else {
      if (requestRef.current) {
        setProgressTime();
        cancelAnimationFrame(requestRef.current);
      }
    }

    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
    };
  }, [isPlaying, audioRef, updateTime, setProgressTime]);

  const handleProgressChange = useCallback(
    (value: number[]) => {
      if (!audioRef.current) return;
      const [rangeProgress] = value;
      const currentTime = audioRef.current.duration * (rangeProgress / 100);
      setProgress(rangeProgress);
      setTime(currentTime);

      if (!isPlaying) {
        audioRef.current.pause();
        audioRef.current.currentTime = currentTime;
      }
    },
    [audioRef, isPlaying],
  );

  return (
    <div className={cn("flex items-center", className)}>
      <div className='f-ui-05 ml-8 mt-2 inline-flex h-full items-center'>
        <time className='f-ui-05 inline-flex w-36 items-center'>{formatTime(time)}</time>
        &nbsp;/&nbsp;
        <time className='f-ui-05 inline-flex items-center text-light'>{formatTime(duration)}</time>
      </div>
      <Range
        className='mx-20 hidden w-full min-w-100 flex-grow md:flex'
        defaultValue={[0]}
        max={100}
        min={0}
        onValueChange={handleProgressChange}
        step={0.01}
        value={[progress]}
      />
    </div>
  );
};

ProgressBar.displayName = "ProgressBar";

interface IAudioPlayerProps {
  className?: string;
  cover?: IImage;
  title?: null | string;
  track: string;
}

const AudioPlayer = ({ title, track, cover, className }: IAudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // states
  const [duration, setDuration] = useState(0);

  const handleLoadedMetadata = useCallback(() => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  }, [audioRef]);

  const onEnded = useCallback(() => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  }, [audioRef, setIsPlaying]);

  useEffect(() => {
    if (!audioRef.current) return;
    const el = audioRef.current;
    if (el.readyState >= 2) {
      handleLoadedMetadata();
    } else {
      el.addEventListener("loadedmetadata", handleLoadedMetadata);
    }
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("loadedmetadata", handleLoadedMetadata);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioRef, handleLoadedMetadata, onEnded]);

  return (
    <>
      <div
        aria-label={`Audio Player ${title ? `- ${title}` : ""}`}
        className={cn(className, "flex items-center gap-20 rounded-sm border p-12 lg:p-20")}
        role='region'
      >
        {isValidImage(cover) && (
          <AspectRatio className='w-80 shrink-0 border md:w-100' ratio='1x1'>
            <Image sizes='100px' {...cover} />
          </AspectRatio>
        )}
        <audio aria-hidden='true' className='hidden' ref={audioRef} src={track} />
        <div className='flex-1 space-y-12 lg:space-y-20'>
          {title && <p className='f-ui-01'>{title}</p>}
          <Controls
            {...{
              isPlaying,
              setIsPlaying,
              audioRef,
            }}
            className='flex grow items-center justify-between'
          >
            <ProgressBar className='mr-auto flex flex-1' {...{ audioRef, duration, isPlaying }} />
          </Controls>
        </div>
      </div>
    </>
  );
};

AudioPlayer.displayName = "AudioPlayer";

export { AudioPlayer, Controls, ProgressBar };
